'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeaderProps {
  className?: string;
}

const navigationItems = [
  { name: 'Home', href: '/' },
  { name: 'Sell', href: '/for-sellers' },
  { name: 'Buy', href: '/for-buyers' },
  { name: 'Resources', href: '/market-updates' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

const Header = ({ className = '' }: HeaderProps) => {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }

    return pathname === href || pathname?.startsWith(`${href}/`);
  };

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 bg-card transition-shadow duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-card'
      } ${className}`}
    >
      <div className="border-b border-secondary/20 bg-secondary">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-[11px] uppercase sm:px-6 lg:px-12">
          <a
            href="tel:+19088970464"
            className="flex items-center font-semibold text-accent transition-colors hover:text-primary-foreground"
          >
            <Icon name="PhoneIcon" size={14} className="mr-1.5" />
            (908) 897-0464
          </a>

          <div className="hidden items-center font-semibold text-secondary-foreground sm:flex">
            <span>Cash offers in 24h</span>
            <span className="mx-3 text-accent">✦</span>
            <span>No fees</span>
            <span className="mx-3 text-accent">✦</span>
            <span>Close in 30 days or less</span>
          </div>
        </div>
      </div>

      <div className="bg-card">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-12">
          <Link
            href="/"
            className="flex min-w-0 items-center space-x-3 transition-opacity hover:opacity-90"
          >
            <div className="relative h-10 w-10 flex-shrink-0">
              <AppImage
                src="/assets/images/silk-solutions-logo-burgundy.png"
                alt="Silk Solutions Real Estate logo"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-heading text-xl font-bold leading-tight text-primary sm:text-2xl">
                SILK SOLUTIONS
              </span>
              <span className="text-[11px] font-semibold uppercase leading-tight text-muted-foreground">
                REAL ESTATE
              </span>
            </div>
          </Link>

          <div className="flex flex-shrink-0 items-center gap-3">
            <Link
              href="/for-sellers"
              className="hidden items-center gap-1.5 rounded-md bg-plum px-4 py-2 text-sm font-semibold text-primary-foreground shadow-button transition-colors hover:bg-plum-deep sm:inline-flex"
            >
              Get Cash Offer
              <span aria-hidden>→</span>
            </Link>

            <Link
              href="/contact"
              className="inline-flex rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-plum hover:text-plum sm:hidden"
            >
              Contact
            </Link>
          </div>
        </div>
      </div>

      <nav className="border-t border-border bg-card" aria-label="Main navigation">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-12">
          <div className="flex gap-1 overflow-x-auto py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:justify-center">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors sm:px-4 ${
                  isActive(item.href)
                    ? 'bg-plum text-primary-foreground shadow-button'
                    : 'text-foreground hover:bg-plum-tint hover:text-plum'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
